import React from 'react';
import { Input } from '../ui/Input';
import type { CanadianTermDetails, MortgageType } from '../../types/mortgageTypes';
import {
  computeCanadianRenewalImpactRows,
  computeCanadianRenewalTotalInterestScenario,
  getCanadianRenewalCount,
  getEffectiveCanadianRenewalSegmentRates,
} from '../../utils/canadianRenewalRates';

interface CanadianTermSectionProps {
  termDetails: CanadianTermDetails;
  amortizationYears: number;
  onChange: (details: CanadianTermDetails) => void;
  principal: number;
  mortgageType: MortgageType;
  currentRate: number;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(value);

export const CanadianTermSection: React.FC<CanadianTermSectionProps> = ({
  termDetails,
  amortizationYears,
  onChange,
  principal,
  mortgageType,
  currentRate,
}) => {
  const handleChange = (field: keyof CanadianTermDetails, value: CanadianTermDetails[keyof CanadianTermDetails]) => {
    onChange({
      ...termDetails,
      [field]: value,
    });
  };

  const renewalCount = getCanadianRenewalCount(amortizationYears, termDetails.termLength);
  const totalAmortizationMonths = amortizationYears * 12;
  const termMonths = termDetails.termLength * 12;
  const remainingMonthsAfterTerm = totalAmortizationMonths - termMonths;

  const hasEffectiveRates =
    getEffectiveCanadianRenewalSegmentRates(currentRate, termDetails, amortizationYears) != null;

  const showImpact =
    termDetails.showRenewalScenario &&
    hasEffectiveRates &&
    principal > 0 &&
    remainingMonthsAfterTerm > 0 &&
    renewalCount > 0;

  const renewalImpactRows =
    showImpact && principal > 0
      ? computeCanadianRenewalImpactRows(principal, currentRate, mortgageType, termDetails, amortizationYears)
      : null;

  const interestComparison = showImpact
    ? computeCanadianRenewalTotalInterestScenario(
        principal,
        currentRate,
        mortgageType,
        termDetails,
        amortizationYears
      )
    : null;

  const setRenewalRateAt = (index: number, raw: string) => {
    const N = renewalCount;
    if (N <= 0) return;
    const next: Array<number | undefined> = Array.from({ length: N }, (_, i) => termDetails.renewalRates?.[i]);
    if (raw === '' || raw.trim() === '') {
      next[index] = undefined;
    } else {
      const num = Number(raw);
      if (Number.isNaN(num)) return;
      next[index] = num;
    }
    onChange({
      ...termDetails,
      renewalRates: next,
      renewalRate: undefined,
    });
  };

  const displayRateAt = (index: number): string | number => {
    const v = termDetails.renewalRates?.[index];
    if (v != null && Number.isFinite(v)) return v;
    if (
      (termDetails.renewalRates == null || termDetails.renewalRates.length === 0) &&
      termDetails.renewalRate != null
    ) {
      return termDetails.renewalRate;
    }
    return '';
  };

  return (
    <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-md">
      <h4 className="font-semibold text-indigo-900 mb-3">Canadian Term Details</h4>

      <div className="mb-4 p-3 bg-indigo-100 rounded-md">
        <p className="text-sm text-indigo-900 mb-2">
          <strong>Understanding Terms vs Amortization:</strong>
        </p>
        <ul className="text-xs text-indigo-800 space-y-1 ml-4">
          <li>• <strong>Term</strong>: Length of your mortgage contract ({termDetails.termLength} years)</li>
          <li>• <strong>Amortization</strong>: Total time to pay off your mortgage ({amortizationYears} years)</li>
          <li>• You&apos;ll need to renew your mortgage approximately <strong>{renewalCount} time{renewalCount !== 1 ? 's' : ''}</strong> over the amortization period</li>
          <li>• At each renewal, you&apos;ll negotiate a new rate based on market conditions</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="max-w-[280px]">
          <Input
            label="Term Length"
            type="number"
            value={termDetails.termLength}
            onChange={(e) => handleChange('termLength', Number(e.target.value))}
            suffix="years"
          />
          <p className="text-xs text-indigo-700 mt-1">
            Common: 1, 2, 3, 4, 5 (most popular), 7, or 10 years
          </p>
        </div>
      </div>

      <div className="mt-4 p-3 bg-white border border-indigo-200 rounded-md">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={termDetails.showRenewalScenario}
            onChange={(e) => handleChange('showRenewalScenario', e.target.checked)}
            className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <span className="text-sm text-indigo-900">
            Show renewal scenario in amortization table
          </span>
        </label>
        {termDetails.showRenewalScenario && renewalCount === 0 && (
          <p className="text-xs text-indigo-600 mt-2 ml-6">
            Renewal modeling needs an amortization period longer than your term (for example, 25-year amortization with a 5-year term).
          </p>
        )}
        {termDetails.showRenewalScenario && renewalCount > 0 && !hasEffectiveRates && (
          <p className="text-xs text-indigo-600 mt-2 ml-6">
            Enter at least one expected renewal rate below. Blank fields use the previous renewal rate (or your contract rate before the first renewal).
          </p>
        )}
      </div>

      {termDetails.showRenewalScenario && renewalCount > 0 && (
        <div className="mt-4 space-y-3">
          <p className="text-xs font-medium text-indigo-800">Expected renewal rate at each term end (%)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: renewalCount }, (_, i) => (
              <div key={i} className="max-w-[280px]">
                <Input
                  label={`Renewal at year ${(i + 1) * termDetails.termLength}`}
                  type="number"
                  value={displayRateAt(i)}
                  onChange={(e) => setRenewalRateAt(i, e.target.value)}
                  suffix="%"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {renewalImpactRows && renewalImpactRows.length > 0 && interestComparison && (
        <div className="mt-4 p-4 bg-white border border-indigo-300 rounded-md">
          <h5 className="text-sm font-semibold text-indigo-900 mb-3">Renewal impact by term</h5>

          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-indigo-600 border-b border-indigo-100">
                  <th className="py-2 pr-3">Year</th>
                  <th className="py-2 pr-3">Balance</th>
                  <th className="py-2 pr-3">Payment before</th>
                  <th className="py-2 pr-3">Payment after</th>
                  <th className="py-2">Rates</th>
                </tr>
              </thead>
              <tbody className="text-indigo-900">
                {renewalImpactRows.map((row) => {
                  const payDiff = row.paymentAfterRenewal - row.paymentBeforeRenewal;
                  return (
                    <tr key={row.renewalIndex} className="border-b border-indigo-50">
                      <td className="py-2 pr-3 font-medium">{row.year}</td>
                      <td className="py-2 pr-3 font-mono">{formatCurrency(row.balanceAtRenewal)}</td>
                      <td className="py-2 pr-3 font-mono">{formatCurrency(row.paymentBeforeRenewal)}/mo</td>
                      <td className="py-2 pr-3 font-mono">
                        {formatCurrency(row.paymentAfterRenewal)}/mo
                        {payDiff !== 0 && (
                          <span className={payDiff > 0 ? ' text-orange-600' : ' text-green-700'}>
                            {' '}
                            ({payDiff > 0 ? '+' : ''}
                            {formatCurrency(payDiff)})
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {row.rateBefore}% → {row.rateAfter}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div
              className={`rounded-md p-3 text-center ${
                interestComparison.scenarioInterest - interestComparison.contractOnlyInterest > 0
                  ? 'bg-orange-50'
                  : interestComparison.scenarioInterest - interestComparison.contractOnlyInterest < 0
                  ? 'bg-green-50'
                  : 'bg-indigo-50'
              }`}
            >
              <p className="text-xs text-indigo-600 mb-1">Total interest vs contract rate only (P+I)</p>
              <p
                className={`text-base font-bold ${
                  interestComparison.scenarioInterest - interestComparison.contractOnlyInterest > 0
                    ? 'text-orange-800'
                    : interestComparison.scenarioInterest - interestComparison.contractOnlyInterest < 0
                    ? 'text-green-800'
                    : 'text-indigo-900'
                }`}
              >
                {interestComparison.scenarioInterest - interestComparison.contractOnlyInterest > 0
                  ? `+${formatCurrency(interestComparison.scenarioInterest - interestComparison.contractOnlyInterest)}`
                  : formatCurrency(interestComparison.scenarioInterest - interestComparison.contractOnlyInterest)}
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            The amortization table below applies these renewal rates by segment. Renewal dates are marked with a gold banner.
          </p>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-indigo-200">
        <p className="text-xs text-indigo-700">
          <strong>Note:</strong> The amortization schedule shows markers at each term renewal. In reality, you would
          renegotiate at each renewal, potentially at a different rate.
        </p>
      </div>
    </div>
  );
};
